// Utility to load and parse POI data from CSV
// Maps POI numbers to their categories and products

/**
 * Parse CSV text into structured POI data
 * @param {string} csvText - Raw CSV text content
 * @returns {Object} - Structured data keyed by POI number
 */
export function parsePOIData(csvText) {
  const lines = csvText.split('\n').filter(line => line.trim() && !line.startsWith('Table'))
  
  // Remove header row
  const dataLines = lines.slice(1)
  
  // Group products by POI number
  const poiMap = {}
  
  dataLines.forEach(line => {
    // Parse CSV line: split on commas not inside quotes
    const matches = line.match(/(".*?"|[^,]+)(?=,|$)/g)
    if (!matches || matches.length < 6) return
    
    const [category, poi, secRef, product, icon, link, type] = matches.map(m => m.replace(/^"|"$/g, '').trim())
    
    if (!poi || !poi.startsWith('POI')) return
    
    // Initialize POI entry if not exists
    if (!poiMap[poi]) {
      poiMap[poi] = {
        name: poi,
        category: category,
        secRef: secRef,
        // 'info' = 3D marker click opens URL directly (no POI_Window drill-down)
        // 'standard' = normal POI_Window behaviour
        type: (type && type.trim() === 'info') ? 'info' : 'standard',
        products: []
      }
    }
    
    // Add product to POI
    poiMap[poi].products.push({
      name: product,
      icon: icon.replace('public\\', '/').replace('public/', '/'),
      link: link
    })
  })
  
  return poiMap
}

// Module-level cache — the fetch Promise is reused across all callers so the
// CSV is only downloaded once per page load, regardless of how many components
// call loadPOIData() or how often selectedSec changes.
let _poiDataPromise = null

/**
 * Load POI data from CSV file (cached after first load)
 * @returns {Promise<Object>} - Structured POI data
 */
export async function loadPOIData() {
  if (!_poiDataPromise) {
    _poiDataPromise = (async () => {
      try {
        const response = await fetch('/POI_Data_table_Gold.csv')
        if (!response.ok) {
          throw new Error(`Failed to load POI data: ${response.statusText}`)
        }
        const csvText = await response.text()
        return parsePOIData(csvText)
      } catch (error) {
        _poiDataPromise = null // allow retry on next call
        return {}
      }
    })()
  }
  return _poiDataPromise
}

/**
 * Get POI data for a specific POI node
 * @param {Object} poiDataMap - The parsed POI data map
 * @param {string} poiNodeName - The POI node name (e.g., "POI001", "POI_001")
 * @returns {Object|null} - POI data or null if not found
 */
export function getPOIDataByNode(poiDataMap, poiNodeName) {
  if (!poiDataMap || !poiNodeName) return null
  
  // Extract POI number from node name (handles POI001, POI_001, etc.)
  const match = poiNodeName.match(/POI[_]?(\d+)/i)
  if (!match) return null
  
  const poiNumber = `POI${match[1].padStart(3, '0')}`
  return poiDataMap[poiNumber] || null
}

// Suppress editorial/placeholder content that should not be shown to users
const SUPPRESS_PATTERNS = [
  /^ok to identify/i,
  /^take out\?/i,
  /^leave in or remove\?/i,
  /^combine with/i,
]

function isEditorialContent(text) {
  return SUPPRESS_PATTERNS.some(re => re.test(text.trim()))
}

/**
 * Parse a single CSV line into columns, respecting quoted fields.
 */
function parseCsvLine(line) {
  const cols = []
  let current = ''
  let inQuotes = false
  for (let c = 0; c < line.length; c++) {
    const ch = line[c]
    if (ch === '"') {
      inQuotes = !inQuotes
    } else if (ch === ',' && !inQuotes) {
      cols.push(current)
      current = ''
    } else {
      current += ch
    }
  }
  cols.push(current)
  return cols
}

/**
 * Parse the detail text CSV into three Maps keyed by tag name:
 *   - sectionMap   (Level 1+2): section/subsection name → description  — used by SEC_Window + POI_Window placeholder
 *   - categoryMap  (Level 3): category name  → description  — used by POI_Window (standard POIs)
 *   - productMap   (Level 4): product name   → description  — used by POI_Window (SEC006 per-product)
 * @param {string} csvText - Raw CSV text
 * @returns {{ sectionMap: Map<string,string>, categoryMap: Map<string,string>, productMap: Map<string,string> }}
 */
function parseDetailText(csvText) {
  const sectionMap  = new Map()
  const categoryMap = new Map()
  const productMap  = new Map()
  const lines = csvText.split('\n')

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i]
    if (!line.trim()) continue

    const cols    = parseCsvLine(line)
    const tag     = (cols[0] || '').trim()
    const level   = (cols[1] || '').trim()
    const content = (cols[3] || '').trim()

    if (!tag || !content) continue
    if (isEditorialContent(content)) continue

    if      (level === '1' || level === '2') sectionMap.set(tag, content)
    else if (level === '3') categoryMap.set(tag, content)
    else if (level === '4') productMap.set(tag, content)
  }

  return { sectionMap, categoryMap, productMap }
}

// Module-level cache for detail text
let _detailTextPromise = null

/**
 * Load detail text from POI_Detail Text.csv (cached after first load).
 * @returns {Promise<{ categoryMap: Map<string,string>, productMap: Map<string,string> }>}
 */
export async function loadDetailText() {
  if (!_detailTextPromise) {
    _detailTextPromise = (async () => {
      try {
        const response = await fetch('/POI_Detail%20Text.csv')
        if (!response.ok) throw new Error(`Failed to load detail text: ${response.statusText}`)
        const csvText = await response.text()
        return parseDetailText(csvText)
      } catch (error) {
        _detailTextPromise = null // allow retry
        return { sectionMap: new Map(), categoryMap: new Map(), productMap: new Map() }
      }
    })()
  }
  return _detailTextPromise
}
